import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Upload, Select, message, Card, Space, Tag, Switch, Progress, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
// import ReactQuill from 'react-quill';
import ReactQuill from 'react-quill-new';
import { CollectionFormData } from '../../types/collections';
import productService, { ProductSelector } from '../../services/productService';
import articleService, { ArticleSelector } from '../../services/articleService';
import SectionForm from './SectionForm';
import { uploadSingleFile, deleteFileByUrl } from '../../services/uploadService';
import type { UploadFile } from 'antd/es/upload/interface';

const { Option } = Select;

interface CollectionFormProps {
  initialValues?: Partial<CollectionFormData>;
  onSubmit: (values: CollectionFormData) => void;
  loading?: boolean;
}

interface Section {
  id: number;
  name: string;
  description: string;
  images: string[];
  images_2: string[];
  name_en?: string;
  description_en?: string;
}

const CollectionForm: React.FC<CollectionFormProps> = ({
  initialValues,
  onSubmit,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [quillValue, setQuillValue] = useState('');
  const [quillValueEn, setQuillValueEn] = useState('');
  const [products, setProducts] = useState<ProductSelector[]>([]);
  const [articles, setArticles] = useState<ArticleSelector[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionFormVisible, setSectionFormVisible] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [imageList, setImageList] = useState<string[]>(initialValues?.images || []);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadPercent, setUploadPercent] = useState<number>(0);

  // Fetch danh sách sản phẩm từ API selectors
  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const result = await productService.getProductSelectors();
      setProducts(result.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Lỗi khi tải danh sách sản phẩm');
    } finally {
      setProductsLoading(false);
    }
  };

  // Fetch danh sách bài viết từ API selectors
  const fetchArticles = async () => {
    try {
      setArticlesLoading(true);
      const result = await articleService.getArticleSelectors();
      setArticles(result.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      message.error('Lỗi khi tải danh sách bài viết');
    } finally {
      setArticlesLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchArticles();
  }, []);

  useEffect(() => {
    if (initialValues) {
      const formValues = {
        ...initialValues,
        status: initialValues.status === 'active' // Convert string to boolean for Switch
      };
      form.setFieldsValue(formValues);
      setQuillValue(initialValues.description || '');
      const englishDescription = (initialValues as any)?.translations?.find((t: any) => t.type === 'en')?.description || '';
      setQuillValueEn(englishDescription);
      setImageList(initialValues.images || []);

      // Set English name from translations
      const englishName = (initialValues as any)?.translations?.find((t: any) => t.type === 'en')?.name || '';
      if (englishName) {
        form.setFieldValue('name_en', englishName);
      }

      // Set English description in form field
      if (englishDescription) {
        form.setFieldValue('description_en', englishDescription);
      }
      // Convert sections to new format with id field and English translations
      if (initialValues.sections) {
        const sectionsData = Array.isArray(initialValues.sections)
          ? initialValues.sections.map((section: any, index: number) => {
              // Get English translations for sections
              const englishSection = (initialValues as any)?.translations?.find((t: any) => t.type === 'en')?.sections?.find((s: any) => s.id === (section.id || index));
              return {
                id: section.id || index,
                name: section.name || '',
                description: section.description || '',
                name_en: englishSection?.name || '',
                description_en: englishSection?.description || '',
                images: section.images || [],
                images_2: section.images_2 || []
              };
            })
          : [];
        setSections(sectionsData);
      }
    }
  }, [initialValues, form]);

  useEffect(() => {
    form.setFieldsValue({ images: imageList });
  }, [imageList, form]);

  const handleUpload = async ({ file }: any) => {
    // Upload được xử lý thủ công ở beforeUpload
    if (file.status === 'removed') return;
  };


  const uploadFilesParallel = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setUploadPercent(0);
    const hide = message.loading('Hệ thống đang xử lý ảnh, vui lòng đợi...', 0);
    const total = files.length;
    let completed = 0;
    const onDone = () => {
      completed += 1;
      setUploadPercent(Math.round((completed / total) * 100));
      if (completed === total) {
        hide();
        setIsUploading(false);
        setTimeout(() => setUploadPercent(0), 300);
        message.success('Upload hoàn tất');
      }
    };
    files.forEach(async (f) => {
      try {
        const res: any = await uploadSingleFile(f as any);
        if (res?.status === 'success') {
          const url = res.data.file.url;
          setImageList(prev => [...prev, url]);
        } else {
          message.error(`Upload thất bại: ${f.name}`);
        }
      } catch (e) {
        console.error('Upload failed:', e);
        message.error(`Upload thất bại: ${f.name}`);
      } finally {
        onDone();
      }
    });
  };


  const handleRemove = async (file: any) => {
    try {
      const response = await deleteFileByUrl(file.url);
      if (response.status === 'success') {
        const newImageList = imageList.filter((url: string) => url !== file.url);
        setImageList(newImageList);
        message.success('File deleted successfully');
      } else {
        message.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete file');
      setImageList(imageList.filter((url: string) => url !== file.url));
    }
    return true;
  };


  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // itemRender: có thể custom thêm nếu cần

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Process sections to ensure they have proper id field
      const processedSections = sections.map((section, index) => ({
        ...section,
        id: section.id || index
      }));

      // Generate slug from name
      const generateSlug = (name: string) => {
        return name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
      };

      const data: any = {
        name: values.name,
        description: quillValue,
        images: imageList,
        products: values.products || [],
        sections: processedSections,
        news: values.news || [],
        status: values.status ? 'active' : 'inactive',
        order: values.order || 1,
        slug: generateSlug(values.name),
        translations: [
          {
            type: "en",
            name: values.name_en || '',
            description: quillValueEn,
            sections: processedSections.map(section => ({
              id: section.id,
              name: section.name_en || section.name,
              description: section.description_en || section.description
            }))
          }
        ]
      };

      onSubmit(data);
    } catch (error) {
      message.error('Vui lòng kiểm tra lại thông tin!');
    }
  };

  const handleReset = () => {
    form.resetFields();
    setQuillValue('');
    setQuillValueEn('');
    setSections([]);
    setImageList([]);
  };

  const handleAddSection = () => {
    setEditingSection(null);
    setSectionFormVisible(true);
  };

  const handleEditSection = (section: Section) => {
    setEditingSection(section);
    setSectionFormVisible(true);
  };

  const handleDeleteSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    message.success('Đã xóa phần');
  };

  const handleSectionSubmit = (sectionData: Section) => {
    if (editingSection) {
      // Edit existing section - keep the same id
      const newSections = sections.map((section) => {
        if (section.id === editingSection.id) {
          return { ...sectionData, id: section.id };
        }
        return section;
      });
      setSections(newSections);
      message.success('Đã cập nhật phần');
    } else {
      // Add new section - generate new id based on existing max id + 1
      const maxId = sections.length > 0 ? Math.max(...sections.map(s => s.id)) : -1;
      const newSection = { ...sectionData, id: maxId + 1 };
      setSections([...sections, newSection]);
      message.success('Đã thêm phần mới');
    }
    setSectionFormVisible(false);
    setEditingSection(null);
  };

  const defaultImageUrl = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN';

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label="Tên bộ sưu tập"
          rules={[
            { required: true, message: 'Vui lòng nhập tên bộ sưu tập!' },
            { min: 2, message: 'Tên bộ sưu tập phải có ít nhất 2 ký tự!' },
            { max: 100, message: 'Tên bộ sưu tập không được quá 100 ký tự!' }
          ]}
        >
          <Input placeholder="Nhập tên bộ sưu tập" />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name="name_en"
            label="Tên bộ sưu tập (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập tên bộ sưu tập tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Enter collection name in English"
              style={{
                width: '100%',
                height: 32,
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Row gutter={16} className='mt-4'>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Trạng thái"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="order"
              label="Thứ tự hiển thị"
              initialValue={1}
            >
              <Input type="number" min={1} placeholder="Nhập thứ tự hiển thị" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Mô tả"
          rules={[
            { required: true, message: 'Vui lòng nhập mô tả!' },
          ]}
        >
          <ReactQuill
            theme='snow'
            value={quillValue}
            onChange={val => {
              setQuillValue(val);
            }}
            placeholder='Mô tả chi tiết về bộ sưu tập'
            style={{
              borderRadius: '10px',
              height: '200px',
              marginBottom: '40px',
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' },
                ],
                ['link', 'image'],
                ['clean'],
              ],
            }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'indent',
              'link',
              'image',
            ]}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '60px' }}>
          <Form.Item
            name="description_en"
            label="Mô tả (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
            style={{ marginBottom: '40px' }}
          >
            <ReactQuill
              theme='snow'
              value={quillValueEn}
              onChange={val => {
                setQuillValueEn(val);
              }}
              placeholder='Enter detailed collection description in English'
              style={{
                borderRadius: '10px',
                height: '200px',
                marginBottom: '40px',
                backgroundColor: '#f5f5f5',
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' },
                  ],
                  ['link', 'image'],
                  ['clean'],
                ],
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image',
              ]}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Images"
          name="images"
          rules={[{ required: true, message: 'Please upload at least one image' }]}
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return imageList;
          }}
        >
          <Upload
            listType="picture-card"
            fileList={(imageList || [])
              .map((item: any, index: number): UploadFile => {
                const url: string = typeof item === 'string' ? item : (item?.url || item?.thumbUrl || '');
                return {
                  uid: `-${index}`,
                  name: (url && url.split('/').pop()) || `image-${index}`,
                  status: 'done',
                  url,
                  thumbUrl: url,
                } as UploadFile;
              })
              .filter((f: UploadFile) => !!f.url) as UploadFile[]}
            beforeUpload={(file, fileList) => {
              const first = fileList && fileList[0];
              if (first && (file as any).uid === (first as any).uid) {
                uploadFilesParallel(fileList as unknown as File[]);
              }
              return false;
            }}
            onChange={handleUpload}
            onRemove={handleRemove}
            disabled={isUploading}
            multiple
          >
            {isUploading ? (
              <div style={{ width: 140 }}>
                <div style={{ marginBottom: 8 }}>Đang xử lý...</div>
                <Progress percent={uploadPercent} size="small" status="active" />
              </div>
            ) : (
              imageList.length >= 8 ? null : uploadButton
            )}
          </Upload>
        </Form.Item>


        <Form.Item
          name="products"
          label="Sản phẩm"
        >
          <Select
            mode="multiple"
            placeholder="Chọn sản phẩm"
            allowClear
            showSearch
            loading={productsLoading}
            filterOption={(input, option) => {
              const productName = option?.title || '';
              return productName.toLowerCase().includes(input.toLowerCase());
            }}
            optionFilterProp="children"
          >
            {products.map((product) => (
              <Option
                key={product._id}
                value={product._id}
                title={`${product.name} ${product.materialType}`}
              >
                <div className="flex items-center space-x-2">
                  <img
                    src={product.images?.[0] || defaultImageUrl}
                    alt={product.name}
                    className="object-cover w-6 h-6 rounded"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = defaultImageUrl;
                    }}
                  />
                  <span>{product.name}</span>
                  <Tag color="blue" className="text-xs">{product.materialType}</Tag>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="sections"
          label="Section"
        >
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {sections.length} section đã thêm
              </span>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddSection}
              >
                Thêm section
              </Button>
            </div>

            {sections.map((section, index) => (
              <Card
                key={section.id}
                size="small"
                className="border border-gray-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="mb-2 font-medium text-gray-900">{section.name}</h4>
                    <div
                      className="mb-2 text-sm text-gray-600 line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html: section.description.replace(/<[^>]*>/g, '').substring(0, 100) + '...'
                      }}
                    />
                    <div className="flex items-center space-x-2">
                      <Tag color="blue">{section.images?.length || 0} ảnh</Tag>
                      <Tag color="green">{section.images_2?.length || 0} ảnh 2</Tag>
                    </div>
                  </div>
                  <Space>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditSection(section)}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSection(index)}
                    />
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        </Form.Item>

        <Form.Item
          name="news"
          label="Bài viết"
        >
          <Select
            mode="multiple"
            placeholder="Chọn bài viết"
            allowClear
            showSearch
            loading={articlesLoading}
            filterOption={(input, option) => {
              const articleTitle = option?.title || '';
              return articleTitle.toLowerCase().includes(input.toLowerCase());
            }}
            optionFilterProp="children"
          >
            {articles.map((article) => (
              <Option
                key={article._id}
                value={article._id}
                title={`${article.title} ${article.type}`}
              >
                <div className="flex items-center space-x-2">
                  <img
                    src={article.featuredImage?.url || defaultImageUrl}
                    alt={article.featuredImage?.alt || article.title}
                    className="object-cover w-6 h-6 rounded"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = defaultImageUrl;
                    }}
                  />
                  <span>{article.title}</span>
                  <Tag color="green" className="text-xs">{article.type}</Tag>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item className="mb-0">
          <div className="flex justify-end space-x-2">
            <Button onClick={handleReset}>
              Làm mới
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {initialValues ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </div>
        </Form.Item>
      </Form>

      <SectionForm
        visible={sectionFormVisible}
        onCancel={() => {
          setSectionFormVisible(false);
          setEditingSection(null);
        }}
        onSubmit={handleSectionSubmit}
        initialValues={editingSection}
        loading={loading}
      />
    </>
  );
};

export default CollectionForm;
